import { Injectable } from '@angular/core';
import {EventDispatcherService} from "../event-dispatcher/event-dispatcher.service";
import { environment } from "../../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {
  private interval: number | undefined;
  private connected = false;
  private connecting = false;

  constructor(private readonly eventDispatcher: EventDispatcherService) { }

  /**
   * Normalize socket URLs across environments:
   * - Absolute ws/wss URLs are preserved (e.g. direct routing-test socket).
   * - Relative paths are converted to the current origin so local/tunnel traffic
   *   can go through the dev proxy (/messaging-service with ws=true).
   */
  private resolveSocketUrl(url: string): string {
    if (url.startsWith('ws://') || url.startsWith('wss://')) {
      return url;
    }

    if (url.startsWith('/')) {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${wsProtocol}//${window.location.host}${url}`;
    }

    return url;
  }

  subscribe<T>(eventType: string, handler: (payload: T) => void): string {
    return this.eventDispatcher.subscribeToEvent(`socket.${eventType}`, handler);
  }

  unsubscribe(eventType: string|null, subscriptionId: string|null): void {
    this.eventDispatcher.unsubscribeFromEvent(eventType, subscriptionId);
  }

  subscribeInGroup(subscriptionSetup: () => void): string {
    return this.eventDispatcher.subscribeInGroup(subscriptionSetup);
  }

  unsubscribeByGroup(groupId: string): void {
    return this.eventDispatcher.unsubscribeByGroup(groupId);
  }

  connect(customPath?: string) {
    if (this.connected || this.connecting) {
      return;
    }

    this.connecting = true;

    const socketUrl = customPath
      ? `${environment.baseSocketUrl}${customPath}`
      : environment.socketUrl;

    // Keep explicit socket hosts as-is, but expand relative socket paths to
    // ws(s)://<current-host>/... so the local proxy can route websocket traffic.
    const socket = new WebSocket(this.resolveSocketUrl(socketUrl));

    socket.onopen = () => {
      this.connected = true;
      this.connecting = false;

      console.log('Socket connected');
    };

    socket.onmessage = (notification) => {
      const data = JSON.parse(notification.data);
      this.eventDispatcher.dispatchEvent('socket.' + data.type, data);
      this.eventDispatcher.dispatchEvent('socket.*', data);
    };

    socket.onclose = () => {
      this.disconnect();

      console.log('Socket disconnected');
    };

    socket.onerror = (error) => {
      this.disconnect();

      console.error('Socket error:', error);
    };

    this.eventDispatcher.subscribeToEvent('logout', () => {
      this.disconnect(socket);
    })
  }

  private disconnect(socket?: WebSocket | undefined) {
    socket && socket.close();
    this.connected = false;
    this.connecting = false;
    clearInterval(this.interval);
  }
}
